import { useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { AccessNotice } from '@/components/AccessNotice/AccessNotice';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { FactList } from '@/components/FactList/FactList';
import { LinkDialog } from '@/components/LinkDialog/LinkDialog';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { Spinner } from '@/components/Spinner/Spinner';
import {
  ORGANIZATIONS_QUERY,
  SET_TENANT_ORGANIZATION,
  TENANT_QUERY,
  type OrganizationsResult,
  type SetTenantOrganizationResult,
  type TenantResult,
} from '@/graphql/tenants';
import { formatDate } from '@/lib/format';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { displayName, sortByName } from '@/lib/tenants';
import { useResourceAccess } from '@/lib/use-resource-access';
import { useToast } from '@/toast/useToast';

import styles from './TenantsContainer.module.css';

/** `/tenants/:id`: one tenant's details, and the organization it sits under. */
export function TenantContainer() {
  const { id = '' } = useParams();
  const toast = useToast();
  const { access, refetch: recheckAccess } = useResourceAccess('tenants');
  const canRead = access === 'read' || access === 'full';
  const canEdit = access === 'full';

  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const { data, loading, error } = useQuery<TenantResult>(TENANT_QUERY, {
    variables: { id },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  const organizations = useQuery<OrganizationsResult>(ORGANIZATIONS_QUERY, {
    skip: !linking,
    fetchPolicy: 'cache-and-network',
  });
  const [setOrganization, { loading: saving }] =
    useMutation<SetTenantOrganizationResult>(SET_TENANT_ORGANIZATION);

  const back = <Link to="/tenants">← All tenants</Link>;

  if (!canRead) {
    return <AccessNotice area="tenants" onRetry={recheckAccess} state={access as 'checking'} />;
  }

  if (error && !data) {
    return (
      <>
        <PageHeader back={back} title="Tenant" />
        <Callout tone="danger" title="This tenant couldn't be loaded">
          {friendlyError(error)}
        </Callout>
      </>
    );
  }

  if (loading && !data) {
    return (
      <>
        <PageHeader back={back} title="Tenant" />
        <p className={styles.loading} role="status">
          <Spinner /> Loading tenant…
        </p>
      </>
    );
  }

  const tenant = data?.brokerage;
  if (!tenant) {
    return null;
  }
  const name = displayName(tenant);

  const closeLink = () => {
    setLinking(false);
    setLinkError(null);
  };

  const onLink = async (organizationId: string) => {
    setLinkError(null);
    try {
      const result = await setOrganization({
        variables: { tenantId: tenant.id, organizationId: organizationId || null },
      });
      closeLink();
      const organization = result.data?.setTenantOrganization.organization;
      toast.success({
        title: organization
          ? `${name} is now part of ${displayName(organization)}`
          : `${name} no longer has an organization`,
      });
    } catch (cause) {
      setLinkError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        recheckAccess();
      }
    }
  };

  const organizationOptions = sortByName(organizations.data?.organizations ?? []).map(
    (organization) => [organization.id, displayName(organization)] as const,
  );

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          canEdit ? (
            <Button
              loading={linking && !organizations.data}
              onClick={() => setLinking(true)}
              variant="ghost"
            >
              Change organization
            </Button>
          ) : null
        }
        back={back}
        title={name}
      />
      <FactList
        facts={[
          { label: 'Name', value: tenant.name },
          { label: 'Slug', value: tenant.slug, code: true },
          { label: 'app-api id', value: tenant.extAppApiId, code: true },
          {
            label: 'Organization',
            value: tenant.organization ? (
              <Link to={`/organizations/${tenant.organization.id}`}>
                {displayName(tenant.organization)}
              </Link>
            ) : (
              'None'
            ),
          },
          { label: 'Keystone id', value: tenant.id, code: true },
          { label: 'Created', value: formatDate(tenant.createdAt) },
        ]}
        label="Tenant details"
      />

      {linking && organizations.error && !organizations.data ? (
        <div className={styles.section}>
          <Callout tone="danger" title="Organizations couldn't be loaded">
            {friendlyError(organizations.error)}
          </Callout>
        </div>
      ) : null}

      {linking && organizations.data ? (
        <LinkDialog
          busy={saving}
          confirmLabel="Save"
          description={`${name} will sit under the organization you choose.`}
          error={linkError}
          initial={tenant.organization?.id ?? ''}
          label="Organization"
          onCancel={closeLink}
          onSubmit={onLink}
          options={[['', 'No organization'], ...organizationOptions]}
          title="Change organization"
        />
      ) : null}
    </div>
  );
}
